import { i18n } from "@tellescope/i18n";
import type { FreeformFieldOption } from "@tellescope/utils";
import type { InboxChannel, InboxOwnershipFilter, InboxReadStatusFilter } from "./inbox-filters";

/**
 * Inbox statuses, as `{ value, label }` where the **value is the canonical untranslated string**
 * stored on the record and only the label is localized. Translating the value would make the filter
 * match nothing in any non-English locale.
 */
export const getInboxStatusOptions = (): { value: string; label: string }[] => [
  { value: "New", label: i18n.t("New", { ns: "inbox" }) },
  { value: "In Progress", label: i18n.t("In Progress", { ns: "inbox" }) },
  { value: "Resolved", label: i18n.t("Resolved", { ns: "inbox" }) },
];

export const getInboxOwnershipOptions = (): { value: InboxOwnershipFilter; label: string }[] => [
  { value: "all", label: i18n.t("Show All", { ns: "inbox" }) },
  { value: "mine", label: i18n.t("Show Mine", { ns: "inbox" }) },
];

export const getInboxReadStatusOptions = (): { value: InboxReadStatusFilter; label: string }[] => [
  { value: "all", label: i18n.t("All Messages", { ns: "inbox" }) },
  { value: "unread", label: i18n.t("Unread Only", { ns: "inbox" }) },
  { value: "read", label: i18n.t("Read Only", { ns: "inbox" }) },
];

export const getInboxChannelOptions = (): { value: InboxChannel; label: string }[] => [
  { value: "SMS", label: i18n.t("SMS", { ns: "inbox" }) },
  { value: "Chat", label: i18n.t("Chat", { ns: "inbox" }) },
  { value: "Email", label: i18n.t("Email", { ns: "inbox" }) },
  { value: "Zendesk", label: i18n.t("Zendesk", { ns: "inbox" }) },
];

/**
 * Thread fields available to the catch-all condition builder — the ones the standalone controls
 * don't already cover.
 *
 * Read status is deliberately absent: it compiles to a dynamic per-user key, so it cannot be
 * expressed as a field comparison.
 */
export const getInboxFreeformFieldOptions = (): FreeformFieldOption[] => [
  { value: "title", label: i18n.t("Title", { ns: "inbox" }), valueType: "string" },
  { value: "preview", label: i18n.t("Preview", { ns: "inbox" }), valueType: "string" },
  { value: "outboundPreview", label: i18n.t("Outbound Preview", { ns: "inbox" }), valueType: "string" },
  { value: "phoneNumber", label: i18n.t("Phone Number", { ns: "inbox" }), valueType: "string" },
  { value: "enduserPhoneNumber", label: i18n.t("Patient Phone Number", { ns: "inbox" }), valueType: "string" },
  { value: "recentOutboundUserId", label: i18n.t("Recent Outbound User (ID)", { ns: "inbox" }), valueType: "string" },
  {
    value: "recentInboundEnduserId",
    label: i18n.t("Recent Inbound Patient (ID)", { ns: "inbox" }),
    valueType: "string",
  },
  { value: "threadId", label: i18n.t("Thread ID", { ns: "inbox" }), valueType: "string" },
  { value: "outboundTimestamp", label: i18n.t("Last Outbound At", { ns: "inbox" }), valueType: "date" },
];
